/**
 * Food History Page - Shows food consumption history with filtering and pagination
 * Mapped to backend FoodHistoryDTO with exact field names
 * Features: Multi-column sorting, column visibility management, drag-and-drop reordering
 * Supports offline mode with cached data fallback
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Eye, Edit, Trash2, Plus, Download, ArrowUpDown, ArrowUp, ArrowDown, Columns, Eye as EyeIcon, EyeOff, X, GripVertical, WifiOff, ChevronDown, FileText, Sheet } from 'lucide-react';
import { foodHistoryApi, type FoodHistory, shedApi, type Shed, inventoryApi, type Inventory, cattleApi, type Cattle } from '../../services/gaushala/api';
import { offlineFoodHistoryApi } from '../../lib/offline';

// Duration options for the filter dropdown
const DURATION_OPTIONS = ['Morning', 'Afternoon', 'Evening', '1st Half', 'Last Half', 'Full Day'];

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// Define all sortable columns
type SortableColumn = 'shed' | 'feedItem' | 'quantity' | 'duration' | 'date';

// Sort configuration
interface SortConfig {
  column: SortableColumn;
  direction: 'asc' | 'desc';
}

// Column configuration
interface ColumnConfig {
  key: SortableColumn | 'sl' | 'action';
  label: string;
  visible: boolean;
  order: number;
  sortable: boolean;
}

// localStorage version for schema changes
const COLUMNS_VERSION = 1;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Initialize columns from localStorage or defaults
const initializeColumns = (): ColumnConfig[] => {
  try {
    const savedVersion = localStorage.getItem('foodHistoryColumnsVersion');
    const savedColumns = localStorage.getItem('foodHistoryColumns');

    // Check version compatibility
    if (savedVersion === COLUMNS_VERSION.toString() && savedColumns) {
      return JSON.parse(savedColumns);
    }
  } catch (error) {
    console.error('Error loading saved columns:', error);
  }

  // Default column configuration
  return [
    { key: 'sl', label: 'SL.', visible: true, order: 0, sortable: false },
    { key: 'shed', label: 'Shed', visible: true, order: 1, sortable: true },
    { key: 'feedItem', label: 'Feed Item', visible: true, order: 2, sortable: true },
    { key: 'quantity', label: 'Quantity', visible: true, order: 3, sortable: true },
    { key: 'duration', label: 'Duration', visible: true, order: 4, sortable: true },
    { key: 'date', label: 'Date', visible: true, order: 5, sortable: true },
    { key: 'action', label: 'Action', visible: true, order: 6, sortable: false },
  ];
};

export default function FoodHistory() {
  const navigate = useNavigate();
  const [foodHistory, setFoodHistory] = useState<FoodHistory[]>([]);
  const [filteredData, setFilteredData] = useState<FoodHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [filterLivestockId, setFilterLivestockId] = useState('');
  const [filterShedId, setFilterShedId] = useState('');
  const [filterInventoryId, setFilterInventoryId] = useState('');
  const [filterDuration, setFilterDuration] = useState('');

  // Dropdown data for filters
  const [sheds, setSheds] = useState<Shed[]>([]);
  const [inventoryItems, setInventoryItems] = useState<Inventory[]>([]);
  const [cattle, setCattle] = useState<Cattle[]>([]);

  // Table customization states
  const [sortConfig, setSortConfig] = useState<SortConfig[]>([]);
  const [columns, setColumns] = useState<ColumnConfig[]>(initializeColumns);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<SortableColumn | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<SortableColumn | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Debounce timer ref for search
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ============================================================================
  // COLUMN MANAGEMENT FUNCTIONS
  // ============================================================================

  // Save columns to localStorage
  const saveColumnsToStorage = (cols: ColumnConfig[]) => {
    localStorage.setItem('foodHistoryColumns', JSON.stringify(cols));
    localStorage.setItem('foodHistoryColumnsVersion', COLUMNS_VERSION.toString());
  };

  // Get visible columns sorted by order
  const getVisibleColumns = (): ColumnConfig[] => {
    return columns
      .filter(col => col.visible)
      .sort((a, b) => a.order - b.order);
  };

  // Toggle column visibility
  const toggleColumnVisibility = (key: ColumnConfig['key']) => {
    // Action column must always be visible
    if (key === 'action') return;

    const updated = columns.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    );
    setColumns(updated);
    saveColumnsToStorage(updated);
  };

  // Reset columns to default
  const resetColumns = () => {
    const defaultCols: ColumnConfig[] = [
      { key: 'sl', label: 'SL.', visible: true, order: 0, sortable: false },
      { key: 'shed', label: 'Shed', visible: true, order: 1, sortable: true },
      { key: 'feedItem', label: 'Feed Item', visible: true, order: 2, sortable: true },
      { key: 'quantity', label: 'Quantity', visible: true, order: 3, sortable: true },
      { key: 'duration', label: 'Duration', visible: true, order: 4, sortable: true },
      { key: 'date', label: 'Date', visible: true, order: 5, sortable: true },
      { key: 'action', label: 'Action', visible: true, order: 6, sortable: false },
    ];
    setColumns(defaultCols);
    saveColumnsToStorage(defaultCols);
    setSortConfig([]);
  };

  // ============================================================================
  // DRAG-AND-DROP HANDLERS
  // ============================================================================

  const handleDragStart = (column: SortableColumn) => {
    setDraggedColumn(column);
  };

  const handleDragOver = (e: React.DragEvent, column: SortableColumn) => {
    e.preventDefault();
    setDragOverColumn(column);
  };

  const handleDrop = (e: React.DragEvent, targetColumn: SortableColumn) => {
    e.preventDefault();

    if (!draggedColumn || draggedColumn === targetColumn) {
      setDraggedColumn(null);
      setDragOverColumn(null);
      return;
    }

    const draggedIndex = columns.findIndex(col => col.key === draggedColumn);
    const targetIndex = columns.findIndex(col => col.key === targetColumn);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedColumn(null);
      setDragOverColumn(null);
      return;
    }

    // Create new order
    const newColumns = [...columns];
    const [draggedCol] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(targetIndex, 0, draggedCol);

    // Update order values
    const updatedColumns = newColumns.map((col, index) => ({
      ...col,
      order: index,
    }));

    setColumns(updatedColumns);
    saveColumnsToStorage(updatedColumns);
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  // ============================================================================
  // SORTING FUNCTIONS
  // ============================================================================

  // Handle column sort
  const handleSort = (column: SortableColumn, shiftKey: boolean) => {
    // Check if column is already sorted
    const existingIndex = sortConfig.findIndex(sc => sc.column === column);

    if (!shiftKey) {
      // Single sort mode
      if (existingIndex !== -1) {
        const existing = sortConfig[existingIndex];
        if (existing.direction === 'asc') {
          // Change to desc
          setSortConfig([{ column, direction: 'desc' }]);
        } else {
          // Clear sort
          setSortConfig([]);
        }
      } else {
        // Add new asc sort
        setSortConfig([{ column, direction: 'asc' }]);
      }
    } else {
      // Multi-sort mode (Shift+Click)
      if (existingIndex !== -1) {
        const existing = sortConfig[existingIndex];
        if (existing.direction === 'asc') {
          // Change to desc
          const updated = [...sortConfig];
          updated[existingIndex] = { column, direction: 'desc' };
          setSortConfig(updated);
        } else {
          // Remove this sort
          setSortConfig(sortConfig.filter(sc => sc.column !== column));
        }
      } else {
        // Add new asc sort
        setSortConfig([...sortConfig, { column, direction: 'asc' }]);
      }
    }
  };

  // Get sort indicator for a column
  const getSortIndicator = (column: SortableColumn) => {
    const index = sortConfig.findIndex(sc => sc.column === column);
    if (index === -1) return <ArrowUpDown className="h-3 w-3 text-gray-400" />;

    const config = sortConfig[index];
    const Icon = config.direction === 'asc' ? ArrowUp : ArrowDown;

    return (
      <div className="flex items-center gap-1">
        <Icon className="h-3 w-3 text-blue-600" />
        {sortConfig.length > 1 && (
          <span className="text-xs font-semibold text-blue-600 bg-blue-100 rounded-full w-4 h-4 flex items-center justify-center">
            {index + 1}
          </span>
        )}
      </div>
    );
  };

  // Apply sorting to data
  const applySorting = (data: FoodHistory[]): FoodHistory[] => {
    if (sortConfig.length === 0) return data;

    return [...data].sort((a, b) => {
      for (const config of sortConfig) {
        let comparison = 0;

        switch (config.column) {
          case 'shed':
            comparison = (a.shed?.shedName || '').localeCompare(b.shed?.shedName || '');
            break;
          case 'feedItem':
            comparison = (a.inventory?.itemName || '').localeCompare(b.inventory?.itemName || '');
            break;
          case 'quantity':
            comparison = (a.consumeQuantity || 0) - (b.consumeQuantity || 0);
            break;
          case 'duration':
            comparison = a.duration.localeCompare(b.duration);
            break;
          case 'date':
            comparison = new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime();
            break;
        }

        if (comparison !== 0) {
          return config.direction === 'asc' ? comparison : -comparison;
        }
      }

      return 0;
    });
  };

  // ============================================================================
  // CELL RENDERING
  // ============================================================================

  const renderCellContent = (column: ColumnConfig['key'], item: FoodHistory, index: number) => {
    switch (column) {
      case 'sl':
        return startIndex + index + 1;

      case 'shed':
        return (
          <div className="flex flex-col">
            <span className="font-medium">{item.shed?.shedName || 'Unknown'}</span>
            <span className="text-xs text-gray-500">{item.shed?.shedNumber || `ID: ${item.shedId}`}</span>
          </div>
        );

      case 'feedItem':
        return (
          <div className="flex flex-col">
            <span className="font-medium">{item.inventory?.itemName || 'Unknown'}</span>
            <span className="text-xs text-gray-500">{item.inventory?.typeName || ''} {item.inventory?.unitName ? `(${item.inventory.unitName})` : ''}</span>
          </div>
        );

      case 'quantity':
        return `${item.consumeQuantity?.toFixed(1) || '0.0'} ${item.inventory?.unitName || 'units'}`;

      case 'duration':
        return item.duration;

      case 'date':
        return formatDate(item.date);

      case 'action':
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleView(item.id!)}
              className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
              title="View"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleEdit(item.id!)}
              className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
              title="Edit"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDelete(item.id!)}
              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  // ============================================================================
  // CLICK-OUTSIDE HANDLER
  // ============================================================================

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.column-selector-container')) {
        setShowColumnSelector(false);
      }
    };

    if (showColumnSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnSelector]);

  // ============================================================================
  // FILTER DROPDOWN DATA FETCHING
  // ============================================================================

  // Fetch dropdown data for filters when filter box is opened
  useEffect(() => {
    if (showFilters) {
      const fetchFilterData = async () => {
        try {
          // Fetch sheds, inventory, and cattle in parallel
          const [shedsResult, inventoryResult, cattleResult] = await Promise.all([
            shedApi.getAllSheds(0, 200),
            inventoryApi.getAllInventory(0, 500),
            cattleApi.getAllCattle(0, 500),
          ]);

          if (shedsResult.success && shedsResult.data) {
            setSheds(shedsResult.data.content || []);
          }
          if (inventoryResult.success && inventoryResult.data) {
            // Filter to food-related inventory items only
            const foodKeywords = ['feed', 'fodder', 'grass', 'hay', 'grain', 'cereal', 'supplement', 'mineral', 'vitamin', 'concentrate', 'silage', 'wheat', 'maize', 'oats', 'barley', 'salt', 'food', 'pellets', 'meal', 'bran', 'seeds', 'vegetables', 'greens', 'legume', 'molasses', 'oil', 'crop', 'straw', 'dry grass', 'unprocessed', 'ready food'];
            const allItems = inventoryResult.data.content || [];
            const foodItems = allItems.filter(item => {
              const name = (item.itemName || '').toLowerCase();
              const type = (item.inventoryTypeName || '').toLowerCase();
              return foodKeywords.some(kw => name.includes(kw) || type.includes(kw));
            });
            setInventoryItems(foodItems.length > 0 ? foodItems : allItems);
          }
          if (cattleResult.success && cattleResult.data) {
            setCattle(cattleResult.data.content || []);
          }
        } catch (err) {
          console.error('[FoodHistory] Error fetching filter dropdown data:', err);
        }
      };
      fetchFilterData();
    }
  }, [showFilters]);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  // Fetch data when page, page size, or filters change
  useEffect(() => {
    fetchFoodHistory();
  }, [currentPage, entriesPerPage]);

  // Debounced search: when searchTerm changes, wait 400ms then fetch
  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchFoodHistory();
    }, 400);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchTerm]);

  const fetchFoodHistory = async () => {
    setLoading(true);
    setError(null);

    const isOnline = navigator.onLine;

    try {
      if (isOnline) {
        let result;

        if (searchTerm.trim()) {
          // Use server-side search when search term is present
          result = await foodHistoryApi.searchFoodHistory(searchTerm.trim(), currentPage - 1, entriesPerPage);
        } else {
          // Regular paginated fetch
          result = await foodHistoryApi.getAllFoodHistory(currentPage - 1, entriesPerPage);
        }

        if (result.success && result.data) {
          setFoodHistory(result.data.content || []);
          setFilteredData(result.data.content || []);
          setTotalPages(result.data.totalPages || 1);
          setTotalItems(result.data.totalElements || 0);
          console.log(`[FoodHistory] Online - Loaded ${result.data.content?.length || 0} records${searchTerm ? ` (search: "${searchTerm}")` : ''}`);
        } else {
          console.warn('[FoodHistory] Server request failed, trying cache...');
          await fetchFromCache();
        }
      } else {
        // Offline: Load from cache
        await fetchFromCache();
      }
    } catch (error) {
      console.error('Error fetching food history:', error);
      // Try cache fallback on any error
      await fetchFromCache();
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch food history from offline cache
   */
  const fetchFromCache = async () => {
    try {
      console.log('[FoodHistory] Loading from offline cache...');
      const cachedData = await offlineFoodHistoryApi.getAll(0, 1000);

      if (cachedData && cachedData.length > 0) {
        let filtered = cachedData as FoodHistory[];

        // Apply client-side search for offline mode
        if (searchTerm.trim()) {
          const lowerSearch = searchTerm.toLowerCase();
          filtered = filtered.filter(item => {
            const dateFormats: string[] = [];
            if (item.date) {
              const d = new Date(item.date);
              if (!isNaN(d.getTime())) {
                const day = d.getDate();
                const dayPadded = String(day).padStart(2, '0');
                const month0 = d.getMonth();
                const year = d.getFullYear();
                const monthPadded = String(month0 + 1).padStart(2, '0');
                const shortMonth = d.toLocaleDateString('en-US', { month: 'short' }).toLowerCase();
                const longMonth = d.toLocaleDateString('en-US', { month: 'long' }).toLowerCase();
                dateFormats.push(
                  `${shortMonth} ${day}, ${year}`,
                  `${longMonth} ${day}, ${year}`,
                  `${day} ${longMonth} ${year}`,
                  `${day} ${shortMonth} ${year}`,
                  `${dayPadded}-${shortMonth}-${year}`,
                  `${year}-${monthPadded}-${dayPadded}`,
                  `${month0 + 1}/${day}/${year}`,
                  `${day}/${month0 + 1}/${year}`,
                  String(year),
                  shortMonth,
                  longMonth,
                );
              }
            }
            return (
              item.shed?.shedName?.toLowerCase().includes(lowerSearch) ||
              item.shed?.shedNumber?.toLowerCase().includes(lowerSearch) ||
              item.inventory?.itemName?.toLowerCase().includes(lowerSearch) ||
              item.inventory?.typeName?.toLowerCase().includes(lowerSearch) ||
              item.consumeQuantity?.toString().includes(lowerSearch) ||
              item.duration?.toLowerCase().includes(lowerSearch) ||
              item.comments?.toLowerCase().includes(lowerSearch) ||
              dateFormats.some(s => s.includes(lowerSearch)) ||
              (item.date?.toLowerCase().includes(lowerSearch))
            );
          });
        }

        console.log(`[FoodHistory] Offline - Loaded ${filtered.length} records from cache`);
        setFoodHistory(filtered);
        setFilteredData(filtered);
        setTotalPages(1);
        setTotalItems(filtered.length);
        setError(null);
      } else {
        console.log('[FoodHistory] No cached data available');
        setError('No cached data available. Please connect to the internet.');
        setFoodHistory([]);
        setFilteredData([]);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (cacheError) {
      console.error('[FoodHistory] Error loading from cache:', cacheError);
      setError('Failed to load food history');
      setFoodHistory([]);
      setFilteredData([]);
    }
  };

  // Apply client-side filters (Filter Box) and sorting on loaded data
  useEffect(() => {
    let filtered = foodHistory;

    // Apply specific filters from the Filter Box
    if (filterLivestockId) {
      filtered = filtered.filter(item =>
        item.livestockId != null && item.livestockId.toString() === filterLivestockId
      );
    }
    if (filterShedId) {
      filtered = filtered.filter(item =>
        item.shedId != null && item.shedId.toString() === filterShedId
      );
    }
    if (filterInventoryId) {
      filtered = filtered.filter(item =>
        item.inventoryId != null && item.inventoryId.toString() === filterInventoryId
      );
    }
    if (filterDuration) {
      filtered = filtered.filter(item =>
        item.duration?.toLowerCase() === filterDuration.toLowerCase()
      );
    }

    // Apply sorting
    filtered = applySorting(filtered);

    setFilteredData(filtered);
  }, [filterLivestockId, filterShedId, filterInventoryId, filterDuration, foodHistory, sortConfig]);

  // Action handlers
  const handleView = (id: number) => {
    navigate(`/gaushala/food-history/view/${id}`);
  };

  const handleEdit = (id: number) => {
    navigate(`/gaushala/food-history/edit/${id}`);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this food history record?')) {
      return;
    }

    try {
      const result = await foodHistoryApi.deleteFoodHistory(id);

      if (result.success) {
        // Refresh the list after successful deletion
        fetchFoodHistory();
      } else {
        alert(result.error || 'Failed to delete food history record');
      }
    } catch (error) {
      console.error('Error deleting food history record:', error);
      alert('Failed to delete food history record');
    }
  };

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buildExportRows = () =>
    filteredData.map((item, index) => ({
      'SL.': startIndex + index + 1,
      'Shed Name': item.shed?.shedName || 'Unknown',
      'Shed Number': item.shed?.shedNumber || '',
      'Feed Item': item.inventory?.itemName || 'Unknown',
      'Feed Type': item.inventory?.typeName || '',
      'Unit': item.inventory?.unitName || '',
      'Quantity': item.consumeQuantity?.toFixed(1) || '0.0',
      'Duration': item.duration || '',
      'Date': formatDate(item.date),
      'Comments': item.comments || '',
    }));

  const handleExportCSV = () => {
    const rows = buildExportRows();
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const escape = (val: unknown) => {
      const s = String(val ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h as keyof typeof r])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `food-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const escape = (val: unknown) => String(val ?? '').replace(/\t/g, ' ');
    const tsv = [headers.join('\t'), ...rows.map(r => headers.map(h => escape(r[h as keyof typeof r])).join('\t'))].join('\n');
    const blob = new Blob(['﻿' + tsv], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `food-history-${new Date().toISOString().slice(0, 10)}.xls`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Calculate display values
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = Math.min(startIndex + filteredData.length, totalItems);

  if (loading && foodHistory.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-500">Loading food history...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Food History</h1>
          <p className="text-gray-600 mt-1">Food consumption history and records</p>
        </div>
        <div className="flex gap-3">
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(prev => !prev)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              Export
              <ChevronDown className="h-4 w-4" />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg transition-colors"
                >
                  <FileText className="h-4 w-4 text-green-600" />
                  Export as CSV
                </button>
                <button
                  onClick={handleExportExcel}
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg transition-colors"
                >
                  <Sheet className="h-4 w-4 text-blue-600" />
                  Export as Excel
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => navigate('/gaushala/food-history/add')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Food Entry
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <span className="text-red-500">⚠</span>
            {error}
          </div>
        </div>
      )}

      {/* Main Content Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Controls */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Left side - Show entries and Filter */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show</span>
                <select
                  value={entriesPerPage}
                  onChange={(e) => {
                    setEntriesPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
              >
                <Filter className="h-4 w-4" />
                Filter Box
              </button>

              {/* Column Management Button */}
              <div className="relative column-selector-container">
                <button
                  onClick={() => setShowColumnSelector(!showColumnSelector)}
                  className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                  title="Manage Columns"
                >
                  <Columns className="h-4 w-4" />
                  Columns ({getVisibleColumns().length})
                </button>

                {/* Column Selector Dropdown */}
                {showColumnSelector && (
                  <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-900">Manage Columns</h3>
                      <button
                        onClick={() => setShowColumnSelector(false)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Column List */}
                    <div className="max-h-96 overflow-y-auto p-2">
                      {columns.map((col) => (
                        <div
                          key={col.key}
                          className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded transition-colors"
                        >
                          {/* Order number */}
                          <span className="text-xs font-mono text-gray-400 w-6">
                            {col.order + 1}
                          </span>

                          {/* Checkbox */}
                          <button
                            onClick={() => toggleColumnVisibility(col.key)}
                            className="flex items-center gap-2 flex-1 text-left"
                            disabled={col.key === 'action'}
                          >
                            {col.visible ? (
                              <EyeIcon className="h-4 w-4 text-blue-600" />
                            ) : (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            )}
                            <span
                              className={`text-sm ${
                                col.visible ? 'text-gray-900' : 'text-gray-400'
                              } ${col.key === 'action' ? 'font-semibold' : ''}`}
                            >
                              {col.label}
                            </span>
                          </button>

                          {/* Action column note */}
                          {col.key === 'action' && (
                            <span className="text-xs text-gray-400 italic">Always visible</span>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
                      <span className="text-sm text-gray-600">
                        {getVisibleColumns().length} of {columns.length} visible
                      </span>
                      <button
                        onClick={resetColumns}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                      >
                        Reset to Default
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right side - Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filter Box */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Livestock Filter - Hidden for now, unhide when needed
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Livestock</label>
                  <select
                    value={filterLivestockId}
                    onChange={(e) => setFilterLivestockId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">All Livestock</option>
                    {cattle.map((c) => (
                      <option key={c.id} value={c.id?.toString() || ''}>
                        {c.name || c.uniqueAnimalId || `Cattle #${c.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                */}

                {/* Shed Filter - Dropdown */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Shed</label>
                  <select
                    value={filterShedId}
                    onChange={(e) => setFilterShedId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">All Sheds</option>
                    {sheds.map((s) => (
                      <option key={s.id} value={s.id?.toString() || ''}>
                        {s.shedName} ({s.shedNumber})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Inventory Filter - Dropdown */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Feed Item</label>
                  <select
                    value={filterInventoryId}
                    onChange={(e) => setFilterInventoryId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">All Feed Items</option>
                    {inventoryItems.map((inv) => (
                      <option key={inv.id} value={inv.id?.toString() || ''}>
                        {inv.itemName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Duration Filter - Dropdown */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Duration</label>
                  <select
                    value={filterDuration}
                    onChange={(e) => setFilterDuration(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">All Durations</option>
                    {DURATION_OPTIONS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Clear Filters Button */}
              {(filterLivestockId || filterShedId || filterInventoryId || filterDuration) && (
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => {
                      setFilterLivestockId('');
                      setFilterShedId('');
                      setFilterInventoryId('');
                      setFilterDuration('');
                    }}
                    className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="h-3 w-3" />
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {getVisibleColumns().map((col) => (
                  <th
                    key={col.key}
                    draggable={col.sortable}
                    onDragStart={() => col.sortable && handleDragStart(col.key as SortableColumn)}
                    onDragOver={(e) => col.sortable && handleDragOver(e, col.key as SortableColumn)}
                    onDrop={(e) => col.sortable && handleDrop(e, col.key as SortableColumn)}
                    onDragEnd={handleDragEnd}
                    onClick={(e) => col.sortable && handleSort(col.key as SortableColumn, e.shiftKey)}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      col.sortable ? 'cursor-pointer select-none hover:bg-gray-100' : ''
                    } ${
                      draggedColumn === col.key ? 'opacity-50' : ''
                    } ${
                      dragOverColumn === col.key ? 'border-l-2 border-blue-500' : ''
                    }`}
                    title={
                      col.sortable
                        ? `Click to sort, Shift+Click for multi-sort${draggedColumn ? '' : ', Drag to reorder'}`
                        : col.label
                    }
                  >
                    <div className="flex items-center gap-2">
                      {col.sortable && draggedColumn === null && (
                        <GripVertical className="h-3 w-3 text-gray-400" />
                      )}
                      <span>{col.label}</span>
                      {col.sortable && getSortIndicator(col.key as SortableColumn)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={getVisibleColumns().length} className="px-6 py-8 text-center text-gray-500">
                    {loading ? 'Loading...' : 'No food history records found'}
                  </td>
                </tr>
              ) : (
                filteredData.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    {getVisibleColumns().map((col) => (
                      <td
                        key={col.key}
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                      >
                        {renderCellContent(col.key, item, index)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-700">
              Showing {filteredData.length > 0 ? startIndex + 1 : 0} to {endIndex} of {totalItems} entries
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm bg-blue-600 text-white rounded">
                {currentPage}
              </span>
              <span className="text-sm text-gray-500">of {totalPages}</span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
